import type { CSSProperties, ReactNode } from 'react'

/**
 * Titre de section « éditorial » — LE style canonique du site (validé par la
 * cliente sur maquette « Quand l'impossible devient possible ») :
 *   • grand serif Baskerville, casse normale (PAS de majuscules)
 *   • le dernier mot signifiant passe en italique + doré (#C2A66B)
 *   • couleur adaptative : crème sur fond sombre, bleu nuit sur fond clair
 *
 * Utilisé partout pour homogénéiser les titres (home + sous-pages).
 * Régler la taille/allure ICI se répercute sur tout le site.
 */
const GOLD = '#C2A66B'

export default function GrandTitle({
  children,
  dark = true,
  size = 'lg',
  color,
  className,
  style,
  editProps,
  as: Tag = 'h2',
}: {
  children: ReactNode
  dark?: boolean
  size?: 'lg' | 'md'
  color?: string
  className?: string
  style?: CSSProperties
  editProps?: Record<string, string>
  as?: 'h1' | 'h2'
}) {
  const base = color ?? (dark ? '#F7EFE1' : '#021734')
  const fontSize =
    size === 'md'
      ? 'clamp(28px, 1rem + 2.6vw, 52px)'
      : 'clamp(32px, 1rem + 3.4vw, 72px)'

  const text = typeof children === 'string' ? children.trim() : ''
  const words = text ? text.split(/\s+/) : []
  // Dernier token contenant une lettre → on met en valeur de lui jusqu'à la fin
  // (inclut la ponctuation finale « ? » collée après un espace).
  let emphFrom = words.length - 1
  for (let i = words.length - 1; i >= 0; i--) {
    if (/\p{L}/u.test(words[i])) { emphFrom = i; break }
  }

  return (
    <Tag
      {...editProps}
      className={className}
      style={{
        fontFamily: 'var(--font-heading, inherit)',
        fontWeight: 400,
        fontSize,
        lineHeight: 1.1,
        letterSpacing: '0.005em',
        color: base,
        textWrap: 'balance',
        ...style,
      }}
    >
      {words.length > 0
        ? words.map((w, i) => (
            <span key={i} style={i >= emphFrom ? { fontStyle: 'italic', color: GOLD } : undefined}>
              {w}
              {i < words.length - 1 ? ' ' : ''}
            </span>
          ))
        : children}
    </Tag>
  )
}
